/**
 * Simplified SSE (Server-Sent Events) hook for AI chat streaming.
 * @remarks
 * Manages EventSource connections for streaming chat responses from the AI backend.
 * Each query creates a single request/response cycle with no auto-reconnection.
 *
 * Key features:
 * - Single request/response per query (auto-reconnect explicitly disabled)
 * - Type-safe SSE event parsing with discriminated unions
 * - Integration with Apollo reactive variables for state management
 * - Clean connection lifecycle with proper cleanup
 * - Two client-side watchdogs so a stream that dies without a terminal frame
 *   fails loudly instead of hanging (SE-7078, see below)
 *
 * This hook replaces the previous complex implementation that included:
 * - Manual reconnection with exponential backoff (unnecessary)
 * - SSEClient wrapper class (removed)
 * - Background/foreground lifecycle (handled by library)
 * - Zustand chatStore integration (replaced with reactive variables)
 *
 * ## Why there are timeouts here now (SE-7078)
 *
 * This hook used to document "manual timeout handling (trusts backend
 * completion events)" as a deliberate simplification: loading state was cleared
 * only by a `complete` / `end` / `error` frame or the EventSource's own `close`
 * / `error`. **That decision is reversed.** It assumed the backend always
 * terminates the stream, and when that assumption broke the UI had no fallback
 * — the reported symptom was the chat panel cycling between
 * "Understanding your question" and "Finding the right data" forever, with no
 * error and no way back.
 *
 * Trusting the server is not a property the client can enforce, so the client
 * now bounds the wait itself: {@link STREAM_SILENCE_TIMEOUT_MS} (reset by every
 * frame) catches a stream that stops producing output, and
 * {@link STREAM_DEADLINE_MS} is an absolute ceiling that still holds once the
 * server grows a heartbeat. Both budgets are derived from measured behaviour of
 * the live handler — see `../utils/streamTimeouts` for the numbers and their
 * provenance.
 *
 * ## Why `pollingInterval: 0` is passed explicitly (SE-7078)
 *
 * It is load-bearing, not decoration. `react-native-sse` defaults
 * `pollingInterval` to **5000 ms** (`EventSource.js`:
 * `this.interval = options.pollingInterval ?? 5000`), and reopens the
 * connection — re-POSTing the identical question — whenever the request reaches
 * `DONE`. Omitting the option does not disable that; only `0` does, because
 * `_pollAgain(time, allowZero)` schedules a reopen solely when
 * `time > 0 || allowZero`, and both reconnect sites pass `allowZero: false`.
 *
 * The previous code omitted the option while a comment claimed it disabled
 * auto-reconnect (SE-3586). Duplicate answers were rare only because `useChat`
 * closes the stream on a terminal frame before the 5 s timer fires — but on the
 * exact failure this ticket is about (no terminal frame) the client would
 * silently re-issue the query every 5 s while the spinner ran.
 */

import { useReactiveVar } from "@apollo/client";
import React, { useCallback, useEffect, useRef } from "react";
import { AppState, AppStateStatus, Platform } from "react-native";
import EventSource, {
  MessageEvent,
  ErrorEvent,
  ExceptionEvent,
  TimeoutEvent,
} from "react-native-sse";

import { useConnectionOperations } from "./useConnectionOperations";

import { env } from "@/lib/env";
import { QueryRequest } from "../types";
import { isSSEEvent, SSEEvent } from "../types/sseEvents";
import { clearActiveStream, setActiveStream } from "../utils/activeStream";
import {
  createForegroundClock,
  ForegroundClock,
  foregroundElapsed,
  markBackgrounded,
  markForegrounded,
  resetForegroundClock,
} from "../utils/foregroundClock";
import {
  STREAM_DEADLINE_MS,
  STREAM_SILENCE_TIMEOUT_MS,
  StreamTimeoutReason,
} from "../utils/streamTimeouts";

import { connectionStatusVar, ConnectionStatus } from "@/stores/knowledgeGraph";

/**
 * Configuration options for Server-Sent Events connection.
 */
interface UseSSEOptions {
  /** Callback invoked when SSE message is received and parsed */
  onMessage?: (event: SSEEvent) => void;
  /** Callback invoked on SSE connection or parsing error */
  onError?: (error: Error) => void;
  /** Callback invoked when SSE connection is successfully opened */
  onOpen?: () => void;
  /** Callback invoked when SSE connection is closed */
  onClose?: () => void;
  /**
   * Callback invoked when a client-side watchdog gives up on the stream
   * (SE-7078). The stream is already torn down by the time this fires; the
   * caller is responsible for clearing its own loading state and telling the
   * user. Distinct from {@link UseSSEOptions.onError}, which reports something
   * the transport actually said.
   */
  onTimeout?: (reason: StreamTimeoutReason) => void;
  /**
   * Fallback authentication token, used only when a request carries no
   * `accessKey`. Captured at render, so it goes stale (SE-8687) — put a
   * fresh token on the request instead.
   */
  accessToken?: string;
}

/**
 * Return type for useSSE hook.
 */
interface UseSSEReturn {
  /** Send a message to start SSE stream */
  sendMessage: (queryRequest: QueryRequest) => void;
  /** Disconnect the SSE connection */
  disconnect: () => void;
  /**
   * Tear the stream down WITHOUT touching connection status (SE-7078).
   * @remarks
   * For callers reacting to a failure the transport already reported. The
   * transport's `error` listener sets status `error`; routing those callers
   * through {@link UseSSEReturn.disconnect} would immediately overwrite it
   * with `disconnected`, and `ConnectionStatusView` renders nothing for
   * `disconnected` — so the error strip would never appear on the one path it
   * exists for.
   */
  abort: () => void;
  /** Current connection status */
  connectionStatus: string;
  /** Whether connection is active */
  isConnected: boolean;
  /** Whether connection is being established */
  isConnecting: boolean;
  /** Whether connection has error */
  hasError: boolean;
}

/**
 * SSE event names that end the stream.
 * @remarks
 * For these, the event NAME is authoritative and the payload is a bonus — see
 * {@link parseAndHandleEvent}.
 */
const TERMINAL_EVENT_NAMES = new Set(["end", "complete"]);

/**
 * Helper function to parse and handle SSE event data.
 * @param eventData - Raw event data string
 * @param onMessage - Callback to invoke with parsed event
 * @param onError - Callback to invoke on parse error (optional)
 * @param eventName - The SSE `event:` name this payload arrived under, when it
 * arrived as a named event
 * @remarks
 * An unparseable payload is dropped — except on a terminal event name, where
 * the name alone is honoured (SE-7078).
 *
 * That exception closes a hang path of exactly the kind this ticket is about.
 * Completion is normally detected from the *payload* (`{"type":"end"}`), so a
 * terminator whose body does not parse — an empty body, a bare `[DONE]`
 * sentinel, `{}` — was dropped in silence. The stream then ended with the
 * client still believing it was loading, and the spinner ran forever. Since the
 * server has already said "end" in the event name, there is nothing to gain by
 * insisting on the body too.
 */
const parseAndHandleEvent = (
  eventData: string,
  onMessage: (event: SSEEvent) => void,
  onError?: (error: Error) => void,
  eventName?: string
): void => {
  const handleUnusablePayload = (): void => {
    if (eventName && TERMINAL_EVENT_NAMES.has(eventName)) {
      onMessage({ type: eventName } as SSEEvent);
    }
  };

  try {
    const parsed: unknown = eventData ? JSON.parse(eventData) : null;

    if (!isSSEEvent(parsed)) {
      handleUnusablePayload();
      return;
    }

    onMessage(parsed);
  } catch (error) {
    handleUnusablePayload();
    if (onError) {
      onError(error instanceof Error ? error : new Error("Parse error"));
    }
  }
};

/**
 * Floor on a silence-watchdog re-arm delay (SE-7443).
 * @remarks
 * Purely a guard against pathological churn. When the watchdog fires with a
 * millisecond or two of budget left it re-arms for the remainder, and while the
 * app is suspended the foreground clock does not advance — so an unfloored
 * remainder could in principle re-arm itself in a tight loop during the short
 * tail of execution the OS grants a backgrounding app. Overshooting the budget
 * by a quarter of a second is free; a spin is not.
 */
const MIN_SILENCE_REARM_MS = 250;

/**
 * Arms the silence watchdog, and re-arms it instead of firing when the budget
 * has not actually elapsed in FOREGROUND time (SE-7443).
 * @param timerRef - Ref holding the currently-armed silence timer
 * @param clockRef - Ref holding the foreground stopwatch, which every frame the
 * stream produces resets
 * @param onExpired - Invoked once the budget has genuinely elapsed
 * @param delayMs - Wall-clock delay before the next check
 * @remarks
 * The re-arm branch is what makes backgrounding safe. React Native suspends JS
 * while the app is backgrounded and flushes expired timers on resume, so on
 * resume this callback runs having "waited" a wall-clock interval that includes
 * time the user could not see. {@link foregroundElapsed} discounts that,
 * including a background window that is still open — which is what makes the
 * decision correct regardless of whether this callback or the AppState
 * notification comes off the queue first.
 *
 * Recursion rather than `setInterval` because each re-arm is for a computed
 * remainder, not a fixed tick, and because a missed clear on an interval leaks
 * a repeating timer rather than a one-shot.
 *
 * The clock is passed as a REF, not a value: the callback must read whatever
 * the clock is when it fires, and the transitions that matter — the frames that
 * reset it, the app-state changes that open and close its background window —
 * all happen after this call returns.
 */
const armSilenceWatchdog = (
  timerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  clockRef: React.MutableRefObject<ForegroundClock>,
  onExpired: () => void,
  delayMs: number
): void => {
  if (timerRef.current) {
    clearTimeout(timerRef.current);
  }

  timerRef.current = setTimeout(() => {
    timerRef.current = null;

    const remaining =
      STREAM_SILENCE_TIMEOUT_MS -
      foregroundElapsed(clockRef.current, Date.now());

    if (remaining > 0) {
      armSilenceWatchdog(
        timerRef,
        clockRef,
        onExpired,
        Math.max(remaining, MIN_SILENCE_REARM_MS)
      );
      return;
    }

    onExpired();
  }, delayMs);
};

/**
 * Setup event listeners for SSE connection.
 * @param es - EventSource instance
 * @param isMountedRef - Ref to track component mount state
 * @param handlers - Event handler callbacks
 * @param handlers.onMessage - Callback for SSE messages
 * @param handlers.onError - Callback for errors
 * @param handlers.onOpen - Callback when connection opens
 * @param handlers.onClose - Callback when connection closes
 * @param handlers.setConnectionStatus - Function to update connection status
 * @param handlers.onActivity - Called for every frame this stream produces, so
 * the silence watchdog can be reset (SE-7078). Deliberately invoked for ALL
 * traffic — including `citations` and unnamed frames the chat layer ignores —
 * because any byte on the wire proves the stream is still alive.
 * @param handlers.onStreamClosed - Called when the stream the hook still owns
 * closes on its own — i.e. somebody closed it through a path other than
 * `teardownCurrentStream` (SE-7078). Must release ownership and cancel the
 * watchdogs; see the call site for why that matters.
 * @param isCurrent - Predicate reporting whether `es` is still the stream the
 * hook owns; a superseded stream's events are ignored (SE-6900)
 */
const setupEventListeners = (
  es: EventSource,
  isMountedRef: React.MutableRefObject<boolean>,
  handlers: {
    onMessage?: (event: SSEEvent) => void;
    onError?: (error: Error) => void;
    onOpen?: () => void;
    onClose?: () => void;
    setConnectionStatus: (status: ConnectionStatus) => void;
    onActivity: () => void;
    onStreamClosed: () => void;
  },
  /**
   * True only while `es` is still the stream the hook owns. A superseded
   * stream's listeners stay attached until its socket finishes unwinding, so
   * without this every late token from an abandoned answer would append to the
   * message the CURRENT answer is writing (SE-6900).
   */
  isCurrent: () => boolean
): void => {
  const {
    onMessage,
    onError,
    onOpen,
    onClose,
    setConnectionStatus,
    onActivity,
    onStreamClosed,
  } = handlers;

  // True only for traffic belonging to the stream the hook currently owns.
  // Note the ordering everywhere below: activity is recorded only AFTER this
  // passes, so a superseded stream's trailing frames cannot keep the current
  // stream's silence watchdog alive.
  const isLive = () => isMountedRef.current && isCurrent();

  // Named event types that backend sends
  const namedEvents = [
    "start",
    "status",
    "token",
    "end",
    "complete",
    "citations",
  ];

  // Handle open event
  es.addEventListener("open", () => {
    if (!isLive()) return;
    onActivity();
    setConnectionStatus("connected");
    onOpen?.();
  });

  // Setup listeners for all named events
  namedEvents.forEach(eventType => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- EventSource types don't include custom named events
    es.addEventListener(eventType as any, (event: any) => {
      if (!isLive()) return;
      // Recorded before the onMessage bail-out: an unhandled or unparseable
      // frame is still proof the stream is alive (SE-7078).
      onActivity();
      if (!onMessage) return;
      parseAndHandleEvent(event.data, onMessage, undefined, eventType);
    });
  });

  // Handle message events (for backward compatibility and unnamed events)
  es.addEventListener("message", (event: MessageEvent) => {
    if (!isLive()) return;
    onActivity();

    const data = event.data as string;

    // Handle [DONE] signal - ignore it as completion is handled by end event
    if (data === "[DONE]") {
      return;
    }

    try {
      const parsed: unknown = data ? JSON.parse(data) : null;

      if (!isSSEEvent(parsed)) {
        console.warn("[useSSE] Received invalid SSE event:", parsed);
        return;
      }

      onMessage?.(parsed);
    } catch (error) {
      console.error("[useSSE] Failed to parse SSE message:", error);
      onError?.(error instanceof Error ? error : new Error("Parse error"));
    }
  });

  // Handle error events
  es.addEventListener(
    "error",
    (event: ErrorEvent | TimeoutEvent | ExceptionEvent) => {
      if (!isLive()) return;
      onActivity();

      setConnectionStatus("error");

      const errorMessage =
        event.type === "exception"
          ? event.error.message
          : event.type === "timeout"
            ? "Connection timeout"
            : event.message || "Connection error";

      onError?.(new Error(errorMessage));
    }
  );

  // Handle close event
  es.addEventListener("close", () => {
    if (!isLive()) return;
    // Reaching here means the stream we still OWN was closed by someone who
    // did not go through `teardownCurrentStream` — in practice
    // `closeActiveStream()`, used by "New Chat" and the `?q=` auto-send path.
    // The hook's own teardown nulls the ref before calling `close()`, so that
    // path fails `isLive()` above and never lands here. Without this,
    // ownership and the watchdogs would both survive the close, and the
    // silence budget would later fire an error banner over a conversation the
    // user had already replaced (SE-7078).
    onStreamClosed();
    setConnectionStatus("disconnected");
    onClose?.();
  });
};

/**
 * Custom hook for managing Server-Sent Events connection for AI chat streaming.
 * @param options - SSE configuration options
 * @param options.onMessage - Callback invoked when SSE message is received and parsed
 * @param options.onError - Callback invoked on SSE connection or parsing error
 * @param options.onOpen - Callback invoked when SSE connection is successfully opened
 * @param options.onClose - Callback invoked when SSE connection is closed
 * @param options.onTimeout - Callback invoked when a client-side watchdog gives up on the stream (SE-7078)
 * @param options.accessToken - Fallback token for a request that carries no `accessKey` (SE-8687)
 * @returns SSE connection controls and status
 * @example
 * ```tsx
 * const { sendMessage, disconnect, isConnected } = useSSE({
 *   onMessage: (event) => {
 *     if (event.type === 'token') {
 *       console.log('Received token:', event.content);
 *     }
 *   },
 *   onError: (error) => console.error('SSE error:', error),
 *   onTimeout: (reason) => console.warn('SSE gave up:', reason),
 *   accessToken: 'bearer-token',
 * });
 *
 * // Send a query
 * sendMessage({
 *   question: 'What is the weather?',
 *   sessionId: 'session-123',
 * });
 * ```
 */
export const useSSE = ({
  onMessage,
  onError,
  onOpen,
  onClose,
  onTimeout,
  accessToken,
}: UseSSEOptions): UseSSEReturn => {
  const connectionStatus = useReactiveVar(connectionStatusVar);
  const { setConnectionStatus } = useConnectionOperations();

  const eventSourceRef = useRef<EventSource | null>(null);
  const isMountedRef = useRef(true);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Foreground stopwatch backing the silence budget (SE-7443).
   * @remarks
   * A ref, not state: it is read from inside a timer callback, where a state
   * value would be a stale closure and a re-render would buy nothing.
   *
   * Seeded from 0 rather than `Date.now()` because reading the clock during
   * render is impure (and lint-blocked). Nothing reads the elapsed value until
   * `restartSilenceTimer` has reset it against a real timestamp, which
   * `sendMessage` does before a stream can exist — and the app-state handler
   * refuses to arm anything while no stream is owned.
   */
  const silenceClockRef = useRef<ForegroundClock>(createForegroundClock(0));

  /**
   * Cancels both stream watchdogs (SE-7078).
   * @remarks Safe to call when no stream is running.
   */
  const clearStreamTimers = useCallback((): void => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (deadlineTimerRef.current) {
      clearTimeout(deadlineTimerRef.current);
      deadlineTimerRef.current = null;
    }
  }, []);

  /**
   * Tears down the stream the hook currently owns, if any.
   * @remarks The ref is cleared BEFORE `close()` so the outgoing stream's
   * listeners already fail their `isCurrent()` check while its close event
   * unwinds — otherwise the dying stream would report "disconnected" over the
   * connection that is replacing it.
   * @returns The stream that was torn down, or null when none was open
   */
  const teardownCurrentStream = useCallback((): EventSource | null => {
    // Unconditional: the watchdogs must die even when there is no stream left
    // to close, or a timer armed by an already-finished stream would later fire
    // against whatever is running then (SE-7078).
    clearStreamTimers();

    const previous = eventSourceRef.current;
    if (!previous) {
      return null;
    }
    eventSourceRef.current = null;
    clearActiveStream(previous);
    previous.close();
    return previous;
  }, [clearStreamTimers]);

  /**
   * Releases ownership of a stream that was closed from outside the hook.
   * @remarks See the `close` listener in {@link setupEventListeners}.
   */
  const releaseClosedStream = useCallback((): void => {
    eventSourceRef.current = null;
    clearStreamTimers();
  }, [clearStreamTimers]);

  /**
   * Gives up on the current stream because a watchdog expired (SE-7078).
   * @param reason - Which budget was exceeded
   * @remarks
   * Tears the stream down FIRST, so the `isCurrent()` guard in
   * {@link setupEventListeners} is already false while the socket unwinds and
   * the dying stream cannot report over the state being set here.
   *
   * The status goes to `disconnected`, NOT `error`, even though this is a
   * failure. `ConnectionStatusView` renders a generic "Connection error" strip
   * for `error`, and the caller is about to publish a far more specific
   * message through `onTimeout` — two near-identical banners stacked on each
   * other is worse than one good one. `disconnected` is also simply accurate:
   * the stream is gone.
   *
   * `onTimeout` must be referentially stable. It sits in this callback's
   * dependencies, and `restartSilenceTimer` depends on this callback, and
   * `setupEventListeners` captures that ONCE per `EventSource` — so an inline
   * arrow here would freeze each stream's watchdog on the callback identity
   * from the render that created it.
   */
  const failStreamByTimeout = useCallback(
    (reason: StreamTimeoutReason): void => {
      if (!isMountedRef.current) {
        return;
      }
      if (!teardownCurrentStream()) {
        // Nothing was running — nothing to give up on. This also absorbs the
        // case where `new EventSource("")` threw because the endpoint env var
        // is unset: the timers are armed before construction, so without this
        // they would fire a phantom error against a stream that never existed.
        return;
      }
      setConnectionStatus("disconnected");
      onTimeout?.(reason);
    },
    [teardownCurrentStream, setConnectionStatus, onTimeout]
  );

  /**
   * (Re)starts the silence watchdog (SE-7078).
   * @remarks
   * Called on every frame the owned stream produces, so the budget measures a
   * gap between frames rather than total elapsed time. The absolute deadline is
   * deliberately NOT restarted here — that is the whole point of having it.
   *
   * SE-7443: the gap it measures is now a gap in FOREGROUND time. The clock is
   * reset alongside the timer so the two always agree about when this stream
   * last spoke.
   */
  const restartSilenceTimer = useCallback((): void => {
    silenceClockRef.current = resetForegroundClock(
      silenceClockRef.current,
      Date.now()
    );
    armSilenceWatchdog(
      silenceTimerRef,
      silenceClockRef,
      () => failStreamByTimeout("silence"),
      STREAM_SILENCE_TIMEOUT_MS
    );
  }, [failStreamByTimeout]);

  /**
   * Keeps the silence budget honest across a background/foreground round trip
   * (SE-7443).
   * @param nextAppState - The state React Native is transitioning to
   * @remarks
   * Backgrounding opens a window that {@link ForegroundClock} discounts from
   * the silence budget; returning to the foreground closes it and re-arms the
   * watchdog for whatever budget is genuinely left.
   *
   * The re-arm is a belt-and-braces guarantee that a live timer exists, not the
   * mechanism that makes the outcome correct — the timer callback consults the
   * same clock, so a watchdog that fires before this handler runs re-arms
   * itself rather than killing a healthy stream. That ordering is unguaranteed
   * on resume, which is exactly why neither side is allowed to be load-bearing
   * on its own.
   *
   * Only the silence budget is adjusted. The absolute deadline is left running
   * on wall-clock time **on purpose**: it exists to bound the *handler's* work
   * (Lambda hard timeout 300 s + 30 s margin), and that Lambda keeps burning
   * its own clock while the phone is asleep. A stream still open 330 s after
   * dispatch is dead whether or not the user was looking at it, so extending
   * the deadline by the backgrounded duration would keep a corpse alive; firing
   * it on resume is the correct outcome, not the bug.
   */
  const handleAppStateChange = useCallback(
    (nextAppState: AppStateStatus): void => {
      const now = Date.now();

      if (nextAppState !== "active") {
        silenceClockRef.current = markBackgrounded(
          silenceClockRef.current,
          now
        );
        return;
      }

      silenceClockRef.current = markForegrounded(silenceClockRef.current, now);

      // Nothing is streaming — there is no budget to re-arm, and arming one
      // here would fire against whatever runs next.
      if (!eventSourceRef.current) {
        return;
      }

      const remaining =
        STREAM_SILENCE_TIMEOUT_MS -
        foregroundElapsed(silenceClockRef.current, now);

      armSilenceWatchdog(
        silenceTimerRef,
        silenceClockRef,
        () => failStreamByTimeout("silence"),
        Math.max(remaining, MIN_SILENCE_REARM_MS)
      );
    },
    [failStreamByTimeout]
  );

  /**
   * Send a message to start SSE stream.
   * @remarks SE-6900: any stream still in flight is torn down FIRST. Previously
   * the new `EventSource` simply overwrote the ref, leaving the abandoned
   * stream's listeners attached so its remaining tokens kept appending to the
   * message the new answer was writing — one prompt "replaying" another's
   * answer. There is deliberately no `connectionStatus === "connecting"` bail:
   * that value came from a render-time closure, so a stale read dropped the
   * send silently after the caller had already committed the user's bubble and
   * set `isLoading`, hanging the spinner with nothing on the wire.
   * @param queryRequest - Query request parameters
   */
  const sendMessage = useCallback(
    (queryRequest: QueryRequest) => {
      if (!isMountedRef.current) {
        return;
      }

      teardownCurrentStream();
      setConnectionStatus("connecting");

      const sseEndpoint = env.EXPO_PUBLIC_SSE_ENDPOINT || "";
      const url = sseEndpoint;

      // Both watchdogs start at dispatch, not at first frame, so a connection
      // that never opens at all is bounded too (SE-7078).
      restartSilenceTimer();
      deadlineTimerRef.current = setTimeout(() => {
        deadlineTimerRef.current = null;
        failStreamByTimeout("deadline");
      }, STREAM_DEADLINE_MS);

      // SE-8687: authorize with the token the request carries — the caller
      // read it fresh at send time. The `accessToken` option is the render-time
      // value and only a fallback for a request that carries no key.
      const bearerToken = queryRequest.accessKey ?? accessToken;
      const config = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        },
        body: JSON.stringify(queryRequest),
        // Auto-reconnect off: reopening re-POSTs the identical question and
        // yields duplicate responses (SE-3586). This MUST be an explicit 0 —
        // omitting the option leaves the library's 5000 ms default in place,
        // which is what the previous comment here got wrong (SE-7078).
        //
        // Caveat this does NOT cover: the library overwrites its interval from
        // any `retry:` field in the stream, so a handler that emits one
        // re-enables reopening at that value. The current handler does not.
        pollingInterval: 0,
      };

      const es = new EventSource(url, config);

      // Setup all event listeners
      setupEventListeners(
        es,
        isMountedRef,
        {
          onMessage,
          onError,
          onOpen,
          onClose,
          setConnectionStatus,
          onActivity: restartSilenceTimer,
          onStreamClosed: releaseClosedStream,
        },
        () => eventSourceRef.current === es
      );

      eventSourceRef.current = es;
      setActiveStream(es);
    },
    [
      teardownCurrentStream,
      setConnectionStatus,
      onOpen,
      onClose,
      onMessage,
      onError,
      accessToken,
      restartSilenceTimer,
      failStreamByTimeout,
      releaseClosedStream,
    ]
  );

  /**
   * Disconnect the SSE connection.
   * @remarks Cancels both stream watchdogs too (via `teardownCurrentStream`),
   * so a normal completion cannot be followed by a spurious timeout.
   */
  const disconnect = useCallback(() => {
    if (teardownCurrentStream()) {
      setConnectionStatus("disconnected");
      onClose?.();
    }
  }, [teardownCurrentStream, setConnectionStatus, onClose]);

  /**
   * Abandon the stream without reporting a connection status (SE-7078).
   * @remarks See {@link UseSSEReturn.abort}.
   */
  const abort = useCallback(() => {
    teardownCurrentStream();
  }, [teardownCurrentStream]);

  /**
   * Subscribes the silence budget to app lifecycle transitions (SE-7443).
   * @remarks
   * Native only. On web `AppState` is backed by page visibility, where timers
   * are throttled rather than suspended and the stream keeps arriving — the
   * failure this guards against does not exist there, and SE-7443 puts web
   * visibility behaviour explicitly out of scope. This is capability gating
   * (JS suspension is a platform property), not a layout decision.
   *
   * `AppState` is subscribed directly rather than through
   * `useAppStateListener`, which keeps the transition in React state and so
   * re-renders and re-subscribes on every transition. Everything here is
   * ref-based, so a state update would buy nothing; and `restartSilenceTimer`
   * is handed to `setupEventListeners` **once per EventSource**, so callback
   * identity churn mid-stream is a hazard worth not introducing.
   */
  useEffect(() => {
    if (Platform.OS === "web") {
      return undefined;
    }

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange
    );

    return () => {
      subscription.remove();
    };
  }, [handleAppStateChange]);

  // Close SSE connection on unmount - no reactive variable updates needed (SE-3755)
  // Mount-time cleanup in ChatContainer resets connectionStatusVar on next visit
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // SE-7078: a pending watchdog outlives the component otherwise, and would
      // fire against an unmounted hook. Cleared inline rather than through
      // `clearStreamTimers` so this effect keeps an empty dependency array —
      // as a dependency it would re-run whenever that callback's identity
      // changed, and the cleanup closes the LIVE stream, which would abort a
      // healthy answer mid-flight.
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      if (deadlineTimerRef.current) {
        clearTimeout(deadlineTimerRef.current);
        deadlineTimerRef.current = null;
      }
      const es = eventSourceRef.current;
      if (es) {
        eventSourceRef.current = null;
        clearActiveStream(es);
        es.close();
      }
      queueMicrotask(() => {
        connectionStatusVar("disconnected");
      });
    };
  }, []);

  return {
    sendMessage,
    disconnect,
    abort,
    connectionStatus,
    isConnected: connectionStatus === "connected",
    isConnecting: connectionStatus === "connecting",
    hasError: connectionStatus === "error",
  };
};
